import { Field, Checkbox as HeadlessCheckbox, Label } from "@headlessui/react";
import { cva, type VariantProps } from "class-variance-authority";
import { type FC, type HTMLAttributes, type ReactNode, type Ref } from "react";
import { cn } from "../lib/utils";
import { trackingAttrs } from "../utils";
import { Icon } from "./Icon";

const CHECKBOX_SELECTED_CLASSES = "bg-brand-accent outline-[0px]";

const CHECKBOX_DISABLED_CLASSES =
	"cursor-default bg-surface-muted outline-bc-default hover:outline-bc-default";

const CHECKBOX_INTERACTIVE_CLASSES =
	"cursor-pointer hover:outline-bc-stronger data-[focus]:outline-[2px] data-[focus]:outline-bc-focus";

const boxVariants = cva(
	"flex items-center justify-center shrink-0 bg-surface-base shadow-sm transition-colors outline outline-[1px] outline-offset-[-1px] outline-bc-strong",
	{
		variants: {
			size: {
				sm: "w-[16px] h-[16px] rounded-[2.5px]",
				md: "w-[17px] h-[17px] rounded-[3px]",
			},
		},
		defaultVariants: {
			size: "md",
		},
	},
);

type BoxVariantProps = VariantProps<typeof boxVariants>;

type BoxState = { checked: boolean; indeterminate?: boolean; disabled?: boolean };

const boxStateClasses = ({ checked, indeterminate, disabled }: BoxState) => [
	(checked || indeterminate) && CHECKBOX_SELECTED_CLASSES,
	disabled && CHECKBOX_DISABLED_CLASSES,
];

const BoxMark = ({ checked, indeterminate, disabled }: BoxState) =>
	indeterminate ? (
		<span
			aria-hidden="true"
			className={cn("h-[2px] w-[9px] rounded-full bg-surface-base", disabled && "bg-secondary")}
		/>
	) : checked ? (
		<Icon type="check" size="micro" color={disabled ? "secondary" : "inverse"} />
	) : null;

export type CheckboxProps = {
	checked: boolean;

	/** Fired with the next state when the user toggles the box. Omit for a read-only/static box. */
	onChange?: (checked: boolean) => void;

	/**
	 * Renders the mixed/"some selected" bar instead of a checkmark, regardless of `checked`.
	 * Maps to `aria-checked="mixed"`.
	 */
	indeterminate?: boolean;

	disabled?: boolean;

	/**
	 * Box dimensions.
	 * - `sm` — 16×16px (matches the legacy `condensed`/`compact` checkbox)
	 * - `md` — 17×17px (the default control size)
	 * @default "md"
	 */
	size?: BoxVariantProps["size"];

	/**
	 * Optional label rendered beside the box. When provided, the box and label are wrapped in a
	 * Headless `Field` so clicking the label also toggles the box. Compute state-dependent content
	 * in the consumer — the primitive owns no domain styling.
	 */
	label?: ReactNode;

	/** Accessible name. Falls back to the `label` association when omitted. */
	"aria-label"?: string;

	/** Forwarded to the underlying `<span role="checkbox">`. */
	"data-testid"?: string;

	/** Analytics identifier — expands to `data-pendo-id` + `data-amp-track-id`. */
	trackingId?: string;

	className?: string;

	ref?: Ref<HTMLElement>;
};

export const Checkbox: FC<CheckboxProps> = ({
	checked,
	onChange,
	indeterminate,
	disabled,
	size,
	label,
	trackingId,
	className,
	ref,
	"aria-label": ariaLabel,
	"data-testid": testId,
}) => {
	const box = (
		<HeadlessCheckbox
			ref={ref}
			checked={checked}
			indeterminate={indeterminate}
			onChange={onChange}
			disabled={disabled}
			aria-label={ariaLabel}
			data-testid={testId}
			className={cn(
				boxVariants({ size }),
				CHECKBOX_INTERACTIVE_CLASSES,
				boxStateClasses({ checked, indeterminate, disabled }),
				className,
			)}
			{...trackingAttrs(trackingId)}
		>
			<BoxMark checked={checked} indeterminate={indeterminate} disabled={disabled} />
		</HeadlessCheckbox>
	);

	if (label === undefined) return box;

	return (
		<Field className="inline-flex items-center gap-x-[6px]">
			{box}
			<Label className={cn("select-none", disabled ? "cursor-default" : "cursor-pointer")}>
				{label}
			</Label>
		</Field>
	);
};
Checkbox.displayName = "Checkbox";

export type CheckboxGlyphProps = HTMLAttributes<HTMLSpanElement> &
	BoxState & {
		/** @default "md" */
		size?: BoxVariantProps["size"];

		/** Analytics identifier — expands to `data-pendo-id` + `data-amp-track-id`. */
		trackingId?: string;

		ref?: Ref<HTMLSpanElement>;
	};

/**
 * A static box drawn like `Checkbox`, hidden from assistive tech. For rows whose own role already
 * carries the checked state, such as a listbox option.
 */
export const CheckboxGlyph: FC<CheckboxGlyphProps> = ({
	checked,
	indeterminate,
	disabled,
	size,
	trackingId,
	className,
	ref,
	...rest
}) => (
	<span
		ref={ref}
		aria-hidden
		{...rest}
		className={cn(
			boxVariants({ size }),
			boxStateClasses({ checked, indeterminate, disabled }),
			className,
		)}
		{...trackingAttrs(trackingId)}
	>
		<BoxMark checked={checked} indeterminate={indeterminate} disabled={disabled} />
	</span>
);
CheckboxGlyph.displayName = "CheckboxGlyph";
